/**
 * Typed clients for the two APIs the portal uses:
 *   identity — auth.<domain>: sign-in, account, organizations, members, invitations, sessions
 *   portal   — api.<domain>/portal: dashboard, websites, deployments, domains, billing
 */
import { config } from "./config";
import { ApiError, request, type RequestOptions } from "./http";
import { PORTAL, TERMS_REQUIRED, termsRequired } from "./legal-core";
import { Session } from "./session";
import type {
  AccessPreview,
  Account,
  AccountClosure,
  CloseAccountInput,
  ActivityEvent,
  AuditEvent,
  CustomerAuditEvent,
  BillingPeriod,
  BrandingVersion,
  BusinessBranding,
  BusinessProfile,
  ChatButtonPosition,
  EmailPreferences,
  ChangePreview,
  CheckoutResult,
  CountedPage,
  Invoice,
  InvoicePage,
  InvoiceQuery,
  InvoiceSummary,
  NotificationQuery,
  PaymentActivityItem,
  PaymentAttempt,
  PaymentAttemptState,
  PaymentMethod,
  RecoveryCase,
  CustomDomain,
  CustomerAccount,
  Deployment,
  EffectivePermissions,
  GrowthAuditEvent,
  GrowthBoard,
  GrowthBoardFilters,
  GrowthClientEvent,
  GrowthCompetitor,
  GrowthInsight,
  GrowthInsightFilters,
  GrowthInsightRow,
  GrowthNicheCatalogue,
  GrowthProfile,
  GrowthSeverity,
  GrowthCompetitorInput,
  GrowthSource,
  GrowthSourceList,
  GrowthSourceProposal,
  GrowthStepAnswers,
  GrowthUrlCheck,
  GrowthSummary,
  GrowthDraft,
  GrowthTask,
  GrowthTaskAttachment,
  GrowthTaskDetail,
  GrowthWebsite,
  GrowthTaskStatus,
  Invitation,
  LoginResult,
  LegalReadable,
  LegalRegistrationRequirements,
  LegalRequirements,
  Member,
  MfaEnrollment,
  MfaRecoveryCodes,
  MfaRecoveryConfirmation,
  MfaRecoveryStatus,
  MyOrganization,
  NotificationCategory,
  NotificationInbox,
  FormDraft,
  OnboardingCompletion,
  OnboardingState,
  Organization,
  Overview,
  Page,
  PlanCatalogue,
  PermissionInfo,
  PlanKey,
  RestrictionsByRole,
  Role,
  SessionInfo,
  SettingsArea,
  SettingsVersion,
  Site,
  SiteDeletionPolicy,
  SiteAnalytics,
  SiteKind,
  SocialSettings,
  StaticFile,
  Subscription,
  SupportForm,
  SupportSubmission,
  SupportSubmissionInput,
  SupportSubmissionList,
  SupportUpload,
  TokensOut,
  TourOutcome,
  User,
  WhatsAppSettings,
  WhatsNewFeed,
} from "./types";

export const session = new Session(config.authUrl);

/** One attempt: the current access token, or a 401 when there is no session to take one from. */
async function sendAuthed<T>(base: string, path: string, opts: Omit<RequestOptions, "token">): Promise<T> {
  const token = await session.token();
  if (!token)
    throw new ApiError({
      status: 401,
      reason: "TOKEN_MISSING",
      detail: "Sign in to continue",
    });
  // Portal calls say which portal they come from (the policy gate is per portal).
  const options: Omit<RequestOptions, "token"> =
    base === config.apiUrl ? { ...opts, headers: { "X-CortexGrip-Portal": PORTAL, ...opts.headers } } : opts;
  return request<T>(base, path, { ...options, token });
}

/** Waits for `ok`, but gives up (rejects) when the caller cancels the request. */
function abortable(ok: Promise<boolean>, signal?: AbortSignal): Promise<boolean> {
  if (!signal) return ok;
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException("The request was cancelled", "AbortError"));
    if (signal.aborted) return abort();
    signal.addEventListener("abort", abort, { once: true });
    ok.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}

/**
 * Authenticated call: attaches the access token. This is the one place 401s are handled (PHP-3322):
 *
 * * A 401 means the API did nothing, so the call is *replayed once* (same body, same `Idempotency-Key`,
 *   new token) after the session is back: a refresh if the token was merely stale, else the re-login
 *   prompt over the page (<SessionGuard/>). Concurrent 401s share one refresh and one prompt.
 * * A second 401 is reported as is (no prompt loop), and so is the first when the user puts the prompt
 *   off ("Not now") or there is no session at all. Background reads (GETs) do not reopen a prompt the
 *   user put off.
 * * Nothing is ever replayed after any other failure: a timeout, a 5xx or a processor error may have
 *   done the work (money rule: never retry an unknown outcome).
 * * The page is never unmounted: only an explicit sign-out leaves it.
 */
async function authedWithReplay<T>(
  base: string,
  path: string,
  opts: Omit<RequestOptions, "token">,
): Promise<T> {
  try {
    return await sendAuthed<T>(base, path, opts);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    const again = await abortable(
      session.waitForReauth({
        background: (opts.method ?? "GET") === "GET",
        revoked: err.reason === "SESSION_REVOKED",
      }),
      opts.signal,
    );
    if (!again) throw err;
    return sendAuthed<T>(base, path, opts);
  }
}

/** Authenticated call: attaches the access token and replays once after a 401 (see above). */
async function authed<T>(base: string, path: string, opts: Omit<RequestOptions, "token"> = {}): Promise<T> {
  try {
    return await authedWithReplay<T>(base, path, opts);
  } catch (err) {
    // The server refused because mandatory policies are outstanding: let the gate step in.
    if (err instanceof ApiError && err.status === 409 && err.reason === TERMS_REQUIRED)
      termsRequired.notify();
    throw err;
  }
}

const A = config.authUrl;
const P = config.apiUrl;
const enc = encodeURIComponent;

/** An authenticated Portal API call (feature modules such as `lib/domains.ts` build on it). */
export function authedPortal<T>(path: string, opts: Omit<RequestOptions, "token"> = {}): Promise<T> {
  return authed<T>(P, path, opts);
}

// ── Identity: public auth flows ─────────────────────────────────────────────
export const auth = {
  login: (email: string, password: string) =>
    request<LoginResult>(A, "/v1/auth/login", {
      method: "POST",
      body: { email, password },
      credentials: "include",
    }),
  loginMfa: (challenge_token: string, code: { code?: string; recovery_code?: string }) =>
    request<TokensOut>(A, "/v1/auth/login/mfa", {
      method: "POST",
      body: { challenge_token, ...code },
      credentials: "include",
    }),
  register: (
    email: string,
    password: string,
    display_name?: string,
    accepted_documents: { document_key: string; version: string }[] = [],
  ) =>
    request<TokensOut>(A, "/v1/auth/register", {
      method: "POST",
      body: { email, password, display_name: display_name || null, accepted_documents },
      credentials: "include",
    }),
  providers: () => request<{ providers: string[] }>(A, "/v1/auth/providers"),
  oauthUrl: (
    provider: string,
    returnTo: string,
    accepted_documents: { document_key: string; version: string }[] = [],
  ) =>
    `${A}/v1/auth/oauth/${enc(provider)}/authorize?return_to=${enc(returnTo)}&accepted_documents=${enc(JSON.stringify(accepted_documents))}`,
  forgotPassword: (email: string) =>
    request<void>(A, "/v1/auth/password/forgot", { method: "POST", body: { email } }),
  resetPassword: (token: string, password: string) =>
    request<void>(A, "/v1/auth/password/reset", { method: "POST", body: { token, password } }),
  verifyEmail: (token: string) =>
    request<User>(A, "/v1/auth/email/verify", { method: "POST", body: { token } }),
  /** Lost the authenticator and every recovery code: the answer is always 202 (nothing is revealed). */
  startMfaRecovery: (email: string, password: string) =>
    request<{ status: string }>(A, "/v1/auth/mfa/recovery", { method: "POST", body: { email, password } }),
  confirmMfaRecovery: (token: string) =>
    request<MfaRecoveryConfirmation>(A, "/v1/auth/mfa/recovery/confirm", { method: "POST", body: { token } }),
  /** "Keep my account": the link in the closure e-mail (the account cannot sign in meanwhile). */
  cancelAccountClosure: (token: string) =>
    request<void>(A, "/v1/auth/account-closure/cancel", { method: "POST", body: { token } }),
  completeMfaRecovery: (token: string) =>
    request<void>(A, "/v1/auth/mfa/recovery/complete", { method: "POST", body: { token } }),
};

// ── Identity: signed-in ─────────────────────────────────────────────────────
/** Sends `restrictions` only when some role is narrowed, so organization-wide changes stay plain. */
export function withRestrictions<T extends object>(body: T & { restrictions?: RestrictionsByRole }) {
  const { restrictions, ...rest } = body;
  return restrictions && Object.keys(restrictions).length > 0 ? { ...rest, restrictions } : rest;
}

export const identity = {
  me: () => authed<Account>(A, "/v1/me"),
  updateMe: (body: { display_name?: string | null; locale?: string | null }) =>
    authed<Account>(A, "/v1/me", { method: "PATCH", body }),
  changePassword: (current_password: string, new_password: string) =>
    authed<void>(A, "/v1/me/password", { method: "POST", body: { current_password, new_password } }),
  resendVerification: () => authed<void>(A, "/v1/me/email/verification", { method: "POST" }),
  beginMfa: () => authed<MfaEnrollment>(A, "/v1/me/mfa/totp", { method: "POST" }),
  activateMfa: (code: string) =>
    authed<MfaRecoveryCodes>(A, "/v1/me/mfa/totp/activate", { method: "POST", body: { code } }),
  regenerateMfaRecoveryCodes: (code: string) =>
    authed<MfaRecoveryCodes>(A, "/v1/me/mfa/recovery-codes", { method: "POST", body: { code } }),
  disableMfa: (body: { password?: string; code?: string }) =>
    authed<void>(A, "/v1/me/mfa/totp/disable", { method: "POST", body }),
  mfaRecoveryStatus: () => authed<MfaRecoveryStatus>(A, "/v1/me/mfa/recovery"),
  cancelMfaRecovery: () => authed<void>(A, "/v1/me/mfa/recovery/cancel", { method: "POST" }),
  closeAccount: (body: CloseAccountInput) =>
    authed<AccountClosure>(A, "/v1/me/account/closure", { method: "POST", body }),
  /** Everything Identity holds about the signed-in person, as parsed JSON (saved as a file by the caller). */
  exportMyData: () => authed<unknown>(A, "/v1/me/account/export"),
  sessions: () => authed<SessionInfo[]>(A, "/v1/me/sessions"),
  revokeSession: (id: string) => authed<void>(A, `/v1/me/sessions/${enc(id)}`, { method: "DELETE" }),
  revokeOtherSessions: () => authed<unknown>(A, "/v1/me/sessions/revoke-others", { method: "POST" }),

  organizations: () => authed<Page<MyOrganization>>(A, "/v1/organizations", { query: { page_size: 100 } }),
  createOrganization: (name: string) =>
    authed<Organization>(A, "/v1/organizations", { method: "POST", body: { name } }),
  updateOrganization: (orgId: string, body: { name?: string }) =>
    authed<Organization>(A, `/v1/organizations/${enc(orgId)}`, { method: "PATCH", body }),
  /** Closes the business for good (organization:delete). */
  deleteOrganization: (orgId: string) =>
    authed<void>(A, `/v1/organizations/${enc(orgId)}`, { method: "DELETE" }),
  permissions: (orgId: string) =>
    authed<EffectivePermissions>(A, "/v1/me/permissions", { query: { organization_id: orgId } }),

  /** One page of members, newest first. `total` counts every member matching `q` (name or e-mail) and `roleId`. */
  membersPage: (
    orgId: string,
    opts: { q?: string; roleId?: string; pageSize?: number; pageToken?: string | null } = {},
  ) =>
    authed<CountedPage<Member>>(A, `/v1/organizations/${enc(orgId)}/members`, {
      query: { q: opts.q, role_id: opts.roleId, page_size: opts.pageSize, page_token: opts.pageToken },
    }),
  /** Every member (follows `next_page_token`): for name lookups and counts, which must not stop at a page. */
  members: async (orgId: string): Promise<CountedPage<Member>> => {
    const items: Member[] = [];
    let token: string | null = null;
    for (;;) {
      const page: CountedPage<Member> = await identity.membersPage(orgId, {
        pageSize: 200,
        pageToken: token,
      });
      items.push(...page.items);
      token = page.next_page_token;
      if (!token) return { items, next_page_token: null, total: page.total };
    }
  },
  removeMember: (orgId: string, userId: string) =>
    authed<void>(A, `/v1/organizations/${enc(orgId)}/members/${enc(userId)}`, { method: "DELETE" }),
  /** Roles left out of `restrictions` apply to the whole business. */
  setMemberRoles: (orgId: string, userId: string, role_ids: string[], restrictions?: RestrictionsByRole) =>
    authed<void>(A, `/v1/organizations/${enc(orgId)}/members/${enc(userId)}/roles`, {
      method: "PUT",
      body: withRestrictions({ role_ids, restrictions }),
    }),
  /** Dry run of a role change: what someone (or, without `user_id`, an invitee) would gain and lose. */
  previewAccess: (
    orgId: string,
    body: { user_id?: string; role_ids: string[]; restrictions?: RestrictionsByRole },
  ) =>
    authed<AccessPreview>(A, `/v1/organizations/${enc(orgId)}/access-preview`, {
      method: "POST",
      body: withRestrictions(body),
    }),
  /** Identity's permission catalogue; `resource_type` says what each permission can be narrowed to. */
  permissionCatalogue: () => authed<PermissionInfo[]>(A, "/v1/permissions"),
  roles: (orgId: string) => authed<Role[]>(A, `/v1/organizations/${enc(orgId)}/roles`),
  invitations: (orgId: string) => authed<Invitation[]>(A, `/v1/organizations/${enc(orgId)}/invitations`),
  /** One page of pending invitations, newest first; `total` counts those matching `q` and `roleId`. */
  invitationsPage: (
    orgId: string,
    opts: { q?: string; roleId?: string; pageSize?: number; pageToken?: string | null } = {},
  ) =>
    authed<CountedPage<Invitation>>(A, `/v1/organizations/${enc(orgId)}/invitations/page`, {
      query: { q: opts.q, role_id: opts.roleId, page_size: opts.pageSize, page_token: opts.pageToken },
    }),
  invite: (
    orgId: string,
    body: { email: string; role_ids: string[]; name?: string; restrictions?: RestrictionsByRole },
  ) =>
    authed<Invitation>(A, `/v1/organizations/${enc(orgId)}/invitations`, {
      method: "POST",
      body: withRestrictions(body),
    }),
  resendInvitation: (orgId: string, invitationId: string) =>
    authed<Invitation>(A, `/v1/organizations/${enc(orgId)}/invitations/${enc(invitationId)}/resend`, {
      method: "POST",
    }),
  transferOwnership: (orgId: string, body: { email: string; name?: string }) =>
    authed<Invitation>(A, `/v1/organizations/${enc(orgId)}/ownership-transfers`, { method: "POST", body }),
  revokeInvitation: (orgId: string, invitationId: string) =>
    authed<void>(A, `/v1/organizations/${enc(orgId)}/invitations/${enc(invitationId)}`, { method: "DELETE" }),
  acceptInvitation: (token: string) =>
    authed<Organization>(A, "/v1/invitations/accept", { method: "POST", body: { token } }),
  /** The business's audit trail, newest first (audit:read). `action` is a prefix, e.g. `member.`. */
  auditEvents: (
    orgId: string,
    filters: { action?: string; actor_user_id?: string; page_token?: string } = {},
  ) =>
    authed<Page<AuditEvent>>(A, `/v1/organizations/${enc(orgId)}/audit-events`, {
      query: { page_size: 50, ...filters },
    }),
};

// ── Portal API ──────────────────────────────────────────────────────────────
export const portal = {
  registrationRequirements: () =>
    request<LegalRegistrationRequirements>(P, "/v1/legal/registration-requirements"),
  legalRequirements: () =>
    authed<LegalRequirements>(P, "/v1/legal/requirements", { query: { point: "access" } }),
  legalReadable: (versionId: string) => authed<LegalReadable>(P, `/v1/legal/versions/${enc(versionId)}`),
  acceptLegal: (versionIds: string[], location = "access", organizationId?: string) =>
    authed<unknown>(P, "/v1/legal/acceptances", {
      method: "POST",
      body: {
        version_ids: versionIds,
        location,
        organization_id: organizationId ?? null,
        agree: true,
      },
    }),
  legalFile: (versionId: string) =>
    authed<Blob>(P, `/v1/legal/versions/${enc(versionId)}/file?download=true`, { responseType: "blob" }),
  overview: (orgId: string) => authed<Overview>(P, `/v1/organizations/${enc(orgId)}/overview`),

  activity: (
    orgId: string,
    filters: {
      type?: string;
      actor_user_id?: string;
      from?: string;
      to?: string;
      page_token?: string;
    } = {},
  ) =>
    authed<Page<ActivityEvent>>(P, `/v1/organizations/${enc(orgId)}/activity`, {
      query: { page_size: 50, ...filters },
    }),
  auditHistory: (orgId: string, page_token?: string) =>
    authed<{ items: CustomerAuditEvent[]; next_page_token: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/audit-history`,
      { query: { page_size: 50, page_token } },
    ),

  sites: (orgId: string) =>
    authed<{ sites: Site[]; next_page_token: string | null }>(P, `/v1/organizations/${enc(orgId)}/sites`, {
      query: { page_size: 100 },
    }),
  createSite: (orgId: string, body: { name: string; slug?: string; kind?: SiteKind }) =>
    authed<Site>(P, `/v1/organizations/${enc(orgId)}/sites`, { method: "POST", body }),
  site: (siteId: string) => authed<Site>(P, `/v1/sites/${enc(siteId)}`),
  renameSite: (siteId: string, name: string) =>
    authed<Site>(P, `/v1/sites/${enc(siteId)}`, { method: "PATCH", body: { name } }),
  deleteSite: (siteId: string) => authed<void>(P, `/v1/sites/${enc(siteId)}`, { method: "DELETE" }),
  undeleteSite: (siteId: string) => authed<Site>(P, `/v1/sites/${enc(siteId)}/undelete`, { method: "POST" }),
  siteDeletionPolicy: () => authed<SiteDeletionPolicy>(P, "/v1/site-deletion-policy"),
  unpublishSite: (siteId: string) =>
    authed<Site>(P, `/v1/sites/${enc(siteId)}/unpublish`, { method: "POST" }),
  republishSite: (siteId: string) =>
    authed<Site>(P, `/v1/sites/${enc(siteId)}/republish`, { method: "POST" }),

  deployments: (siteId: string) =>
    authed<{ deployments: Deployment[]; next_page_token: string | null }>(
      P,
      `/v1/sites/${enc(siteId)}/deployments`,
      { query: { page_size: 50 } },
    ),
  promote: (siteId: string, deploymentId: string) =>
    authed<Site>(P, `/v1/sites/${enc(siteId)}/deployments/${enc(deploymentId)}/promote`, { method: "POST" }),

  domains: (siteId: string) => authed<{ domains: CustomDomain[] }>(P, `/v1/sites/${enc(siteId)}/domains`),
  addDomain: (siteId: string, hostname: string) =>
    authed<CustomDomain>(P, `/v1/sites/${enc(siteId)}/domains`, { method: "POST", body: { hostname } }),
  verifyDomain: (domainId: string) =>
    authed<CustomDomain>(P, `/v1/domains/${enc(domainId)}/verify`, { method: "POST" }),
  removeDomain: (domainId: string) => authed<void>(P, `/v1/domains/${enc(domainId)}`, { method: "DELETE" }),

  plans: () => request<PlanCatalogue>(P, "/v1/plans"),
  subscription: (orgId: string) => authed<Subscription>(P, `/v1/organizations/${enc(orgId)}/subscription`),
  previewPlanChange: (orgId: string, plan: PlanKey, billing_period: BillingPeriod) =>
    authed<ChangePreview>(P, `/v1/organizations/${enc(orgId)}/subscription/preview`, {
      query: { plan, billing_period },
    }),
  changePlan: (orgId: string, plan: PlanKey, billing_period: BillingPeriod = "monthly") =>
    authed<CustomerAccount>(P, `/v1/organizations/${enc(orgId)}/subscription/plan`, {
      method: "PUT",
      body: { plan, billing_period },
    }),
  /** `idempotencyKey`: one per attempt (`attemptKey`); sending it again returns the upgrade it made. */
  checkout: (
    orgId: string,
    body: { plan: PlanKey; billing_period: BillingPeriod; payment_method_id?: string; new_card?: boolean },
    idempotencyKey?: string,
  ) =>
    authed<CheckoutResult>(P, `/v1/organizations/${enc(orgId)}/subscription/checkout`, {
      method: "POST",
      body,
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
    }),
  /** Where to send the person to set up a UK Direct Debit (the processor's page); back to `return_url`. */
  startDirectDebit: (orgId: string, return_url: string) =>
    authed<{ url: string }>(P, `/v1/organizations/${enc(orgId)}/payment-methods/direct-debit/session`, {
      method: "POST",
      body: { return_url },
    }),
  /** Keep the bank account set up on the processor's page (`session_id` from the return URL). */
  completeDirectDebit: (orgId: string, session_id: string, make_default: boolean) =>
    authed<PaymentMethod>(P, `/v1/organizations/${enc(orgId)}/payment-methods/direct-debit/complete`, {
      method: "POST",
      body: { session_id, make_default },
    }),
  /** An upgrade paid in a wallet's sheet: the payment to have approved there (only authorised). */
  startWalletCheckout: (orgId: string, body: { plan: PlanKey; billing_period: BillingPeriod }) =>
    authed<{ payment_reference: string; client_secret: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/subscription/checkout/wallet`,
      { method: "POST", body },
    ),
  /** Takes the approved payment and starts the plan; safe to repeat. */
  finishWalletCheckout: (orgId: string, payment_reference: string) =>
    authed<CheckoutResult>(P, `/v1/organizations/${enc(orgId)}/subscription/checkout/wallet/finish`, {
      method: "POST",
      body: { payment_reference },
    }),

  paymentMethods: (orgId: string) =>
    authed<{ payment_methods: PaymentMethod[]; provider: string; publishable_key?: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/payment-methods`,
    ),
  /** Starts adding a card: Stripe returns a SetupIntent secret for Stripe.js. */
  preparePaymentMethod: (orgId: string) =>
    authed<{ provider: string; client_secret: string | null; publishable_key: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/payment-methods/setup`,
      { method: "POST" },
    ),
  paymentMethod: (orgId: string, id: string) =>
    authed<PaymentMethod>(P, `/v1/organizations/${enc(orgId)}/payment-methods/${enc(id)}`),
  addPaymentMethod: (
    orgId: string,
    body: {
      token: string;
      holder_name?: string;
      country?: string;
      postcode?: string;
      make_default: boolean;
      /** The customer's consent to keep the card; false = use it for one domain payment only. */
      save?: boolean;
    },
  ) => authed<PaymentMethod>(P, `/v1/organizations/${enc(orgId)}/payment-methods`, { method: "POST", body }),
  makeDefaultPaymentMethod: (orgId: string, id: string) =>
    authed<PaymentMethod>(P, `/v1/organizations/${enc(orgId)}/payment-methods/${enc(id)}/default`, {
      method: "POST",
    }),
  removePaymentMethod: (orgId: string, id: string) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/payment-methods/${enc(id)}`, { method: "DELETE" }),

  /** Every invoice. Lists page instead (`invoicesPage`); this is for what needs them all. */
  invoices: (orgId: string) =>
    authed<{ invoices: Invoice[]; total: number }>(P, `/v1/organizations/${enc(orgId)}/invoices`),
  /** One page of invoices, newest first; `total` counts those matching the filters. */
  invoicesPage: (orgId: string, opts: InvoiceQuery = {}) =>
    authed<InvoicePage>(P, `/v1/organizations/${enc(orgId)}/invoices`, {
      query: {
        page_size: opts.pageSize ?? 20,
        page_token: opts.pageToken,
        status: opts.status,
        service: opts.service,
        issued_from: opts.issuedFrom,
      },
    }),
  /** What the Billing overview needs of the invoices (the newest few, what is owed, an upgrade waiting). */
  invoiceSummary: (orgId: string) =>
    authed<InvoiceSummary>(P, `/v1/organizations/${enc(orgId)}/invoices/summary`),
  invoiceExport: (orgId: string) =>
    authed<Blob>(P, `/v1/organizations/${enc(orgId)}/invoices/export`, { responseType: "blob" }),
  invoice: (orgId: string, id: string) =>
    authed<Invoice>(P, `/v1/organizations/${enc(orgId)}/invoices/${enc(id)}`),
  paymentActivity: (
    orgId: string,
    query: { state?: PaymentAttemptState; page_token?: string; page_size?: number },
  ) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
    const qs = params.size ? `?${params}` : "";
    return authed<{ payment_attempts: PaymentActivityItem[]; next_page_token: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/payment-attempts${qs}`,
    );
  },
  /** The customer approved a payment their bank asked about: the portal checks it and finishes what it was for. */
  confirmPaymentApproval: (orgId: string, attemptId: string) =>
    authed<PaymentAttempt>(P, `/v1/organizations/${enc(orgId)}/payment-attempts/${enc(attemptId)}/confirm`, {
      method: "POST",
    }),
  paymentAttempts: (orgId: string, invoiceId: string) =>
    authed<{ payment_attempts: PaymentAttempt[] }>(
      P,
      `/v1/organizations/${enc(orgId)}/invoices/${enc(invoiceId)}/payment-attempts`,
    ),
  payInvoice: (orgId: string, id: string, payment_method_id?: string) =>
    authed<Invoice>(P, `/v1/organizations/${enc(orgId)}/invoices/${enc(id)}/pay`, {
      method: "POST",
      body: { payment_method_id: payment_method_id ?? null },
    }),

  undoScheduledChange: (orgId: string) =>
    authed<CustomerAccount>(P, `/v1/organizations/${enc(orgId)}/subscription/scheduled-change`, {
      method: "DELETE",
    }),
  updateBillingProfile: (
    orgId: string,
    body: {
      billing_email: string | null;
      company_name: string | null;
      country: string | null;
      version: number;
    },
  ) => authed<CustomerAccount>(P, `/v1/organizations/${enc(orgId)}/account`, { method: "PATCH", body }),

  // ── analytics ──
  siteAnalytics: (siteId: string, range: { days: number } | { from: string; to: string }) =>
    authed<SiteAnalytics>(P, `/v1/sites/${enc(siteId)}/analytics`, { query: range }),
  analyticsExport: (siteId: string, range: { days?: number; from?: string; to?: string }) =>
    authed<Blob>(P, `/v1/sites/${enc(siteId)}/analytics/export`, { query: range, responseType: "blob" }),

  // ── onboarding ──
  onboarding: (orgId: string) => authed<OnboardingState>(P, `/v1/organizations/${enc(orgId)}/onboarding`),
  setChecklistDismissed: (orgId: string, dismissed: boolean) =>
    authed<OnboardingState>(P, `/v1/organizations/${enc(orgId)}/onboarding/checklist`, {
      method: "PUT",
      body: { dismissed },
    }),
  completeChecklist: (orgId: string) =>
    authed<OnboardingCompletion>(P, `/v1/organizations/${enc(orgId)}/onboarding/checklist/completion`, {
      method: "POST",
    }),
  recordTour: (outcome: TourOutcome) =>
    authed<{ outcome: TourOutcome }>(P, "/v1/me/onboarding/tour", { method: "PUT", body: { outcome } }),

  // ── form drafts ──
  formDraft: <T>(orgId: string, form: string) =>
    authed<FormDraft<T> | null>(P, `/v1/organizations/${enc(orgId)}/form-drafts/${enc(form)}`),
  saveFormDraft: <T>(orgId: string, form: string, data: T) =>
    authed<FormDraft<T>>(P, `/v1/organizations/${enc(orgId)}/form-drafts/${enc(form)}`, {
      method: "PUT",
      body: { data },
    }),
  discardFormDraft: (orgId: string, form: string) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/form-drafts/${enc(form)}`, { method: "DELETE" }),

  // ── notifications ──
  notifications: (orgId: string, days: number, opts: NotificationQuery = {}) =>
    authed<NotificationInbox>(P, `/v1/organizations/${enc(orgId)}/notifications`, {
      query: {
        days,
        filter: opts.filter,
        sort: opts.sort,
        page_size: opts.pageSize,
        page_token: opts.pageToken,
      },
    }),
  markNotifications: (orgId: string, body: { keys: string[]; read?: boolean; dismissed?: boolean }) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/notifications/marks`, { method: "POST", body }),
  readAllNotifications: (orgId: string) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/notifications/read-all`, { method: "POST" }),
  setNotificationPreferences: (orgId: string, muted_categories: NotificationCategory[]) =>
    authed<{ muted_categories: NotificationCategory[] }>(
      P,
      `/v1/organizations/${enc(orgId)}/notifications/preferences`,
      { method: "PUT", body: { muted_categories } },
    ),

  // ── what's new ──
  whatsNew: (orgId: string) => authed<WhatsNewFeed>(P, `/v1/organizations/${enc(orgId)}/whats-new`),
  markWhatsNew: (
    orgId: string,
    body: { ids: string[]; seen?: boolean; dismissed?: boolean; snooze_until?: string | null },
  ) => authed<void>(P, `/v1/organizations/${enc(orgId)}/whats-new/marks`, { method: "POST", body }),

  // ── settings ──
  profile: (orgId: string) => authed<BusinessProfile>(P, `/v1/organizations/${enc(orgId)}/profile`),
  saveProfile: (
    orgId: string,
    body: { time_zone: string | null; industry: string | null; currency: string | null },
  ) => authed<BusinessProfile>(P, `/v1/organizations/${enc(orgId)}/profile`, { method: "PUT", body }),
  whatsapp: (orgId: string) => authed<WhatsAppSettings>(P, `/v1/organizations/${enc(orgId)}/whatsapp`),
  saveWhatsapp: (
    orgId: string,
    body: {
      country: string;
      dial_code: string;
      number: string;
      message: string;
      button_enabled: boolean;
      button_position: ChatButtonPosition;
    },
  ) => authed<WhatsAppSettings>(P, `/v1/organizations/${enc(orgId)}/whatsapp`, { method: "PUT", body }),
  publishWhatsapp: (orgId: string) =>
    authed<WhatsAppSettings>(P, `/v1/organizations/${enc(orgId)}/whatsapp/publish`, { method: "POST" }),
  branding: (orgId: string) => authed<BusinessBranding>(P, `/v1/organizations/${enc(orgId)}/branding`),
  saveBrandingColour: (orgId: string, colour_hex: string) =>
    authed<BusinessBranding>(P, `/v1/organizations/${enc(orgId)}/branding/colour`, {
      method: "PUT",
      body: { colour_hex },
    }),
  uploadBrandingLogo: (orgId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return authed<BusinessBranding>(P, `/v1/organizations/${enc(orgId)}/branding/logo`, {
      method: "PUT",
      body: form,
    });
  },
  brandingLogo: (orgId: string) =>
    authed<Blob>(P, `/v1/organizations/${enc(orgId)}/branding/logo`, { responseType: "blob" }),
  brandingHistory: (orgId: string) =>
    authed<{ items: BrandingVersion[] }>(P, `/v1/organizations/${enc(orgId)}/branding/history`),
  social: (orgId: string, businessName?: string) =>
    authed<SocialSettings>(P, `/v1/organizations/${enc(orgId)}/social`, {
      query: businessName ? { business_name: businessName } : undefined,
    }),
  saveSocial: (orgId: string, links: { platform: string; url: string; handle: string }[]) =>
    authed<SocialSettings>(P, `/v1/organizations/${enc(orgId)}/social`, { method: "PUT", body: { links } }),
  publishSocial: (orgId: string) =>
    authed<SocialSettings>(P, `/v1/organizations/${enc(orgId)}/social/publish`, { method: "POST" }),
  /** Every saved change to the business settings, newest first (audit:read). */
  settingsHistory: (orgId: string, filters: { area?: SettingsArea; page_token?: string } = {}) =>
    authed<{ items: SettingsVersion[]; next_page_token: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/settings-history`,
      { query: { page_size: 50, ...filters } },
    ),
  /** Saves a version's values again as a new version (audit:read and organization:update). */
  restoreSettingsVersion: (orgId: string, versionId: string) =>
    authed<{ area: SettingsArea; restored_from: string }>(
      P,
      `/v1/organizations/${enc(orgId)}/settings-history/${enc(versionId)}/restore`,
      { method: "POST" },
    ),
  recoveryCases: (orgId: string) =>
    authed<{ cases: RecoveryCase[] }>(P, `/v1/organizations/${enc(orgId)}/reconciliation-cases`),
  reportRecoveryCase: (
    orgId: string,
    body: {
      resource_type: string;
      resource_id: string;
      operation_id?: string | null;
      provider_ref?: string | null;
      summary: string;
      customer_message: string;
      safe_detail?: Record<string, unknown>;
    },
  ) =>
    authed<RecoveryCase>(P, `/v1/organizations/${enc(orgId)}/reconciliation-cases`, {
      method: "POST",
      body,
    }),
  emailPreferences: (orgId: string) =>
    authed<EmailPreferences>(P, `/v1/organizations/${enc(orgId)}/notifications/email-preferences`),
  saveEmailPreferences: (orgId: string, body: Pick<EmailPreferences, "updates" | "digest_frequency">) =>
    authed<EmailPreferences>(P, `/v1/organizations/${enc(orgId)}/notifications/email-preferences`, {
      method: "PUT",
      body,
    }),

  // Growth Intelligence
  growthNiches: () => request<GrowthNicheCatalogue>(P, "/v1/growth/niches"),
  growthProfile: (orgId: string) =>
    authed<GrowthProfile>(P, `/v1/organizations/${enc(orgId)}/growth/profile`),
  acceptGrowthTerms: (orgId: string) =>
    authed<GrowthProfile>(P, `/v1/organizations/${enc(orgId)}/growth/profile/terms`, { method: "POST" }),
  saveGrowthStep: (orgId: string, step: number, body: GrowthStepAnswers) =>
    authed<GrowthProfile>(P, `/v1/organizations/${enc(orgId)}/growth/profile/steps/${step}`, {
      method: "PUT",
      body,
    }),
  optOutOfGrowth: (orgId: string, version: number) =>
    authed<GrowthProfile>(P, `/v1/organizations/${enc(orgId)}/growth/profile/opt-out`, {
      method: "POST",
      body: { version },
    }),
  completeGrowthProfile: (orgId: string, version: number) =>
    authed<GrowthProfile>(P, `/v1/organizations/${enc(orgId)}/growth/profile/complete`, {
      method: "POST",
      body: { version },
    }),
  requestGrowthSetup: (orgId: string) =>
    authed<{ requested_at: string }>(P, `/v1/organizations/${enc(orgId)}/growth/access-requests`, {
      method: "POST",
    }),
  addCompetitor: (orgId: string, body: GrowthCompetitorInput) =>
    authed<GrowthCompetitor>(P, `/v1/organizations/${enc(orgId)}/growth/competitors`, {
      method: "POST",
      body,
    }),
  /** Check the competitor's website now (the dashboard's reload); the profile comes back with the work queued. */
  checkCompetitor: (orgId: string, competitorId: string) =>
    authed<GrowthProfile>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/competitors/${enc(competitorId)}/check`,
      { method: "POST" },
    ),
  removeCompetitor: (orgId: string, competitorId: string) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/growth/competitors/${enc(competitorId)}`, {
      method: "DELETE",
    }),
  /** The Sources page: the business type's sources that reach this business, and the ones it proposed. */
  growthSources: (orgId: string) =>
    authed<GrowthSourceList>(P, `/v1/organizations/${enc(orgId)}/growth/sources`),
  /** Send a public source for CortexGrip's review; it isn't checked or trusted until it's approved. */
  proposeSource: (orgId: string, body: GrowthSourceProposal) =>
    authed<GrowthSource>(P, `/v1/organizations/${enc(orgId)}/growth/sources`, { method: "POST", body }),
  /** The dialogs' live check: is this a public website that answers? A 422 says why not. */
  checkUrl: (orgId: string, url: string) =>
    authed<GrowthUrlCheck>(P, `/v1/organizations/${enc(orgId)}/growth/url-checks`, {
      method: "POST",
      body: { url },
    }),
  growthAudit: (orgId: string, pageToken?: string) =>
    authed<{ items: GrowthAuditEvent[]; next_page_token: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/audit${pageToken ? `?page_token=${enc(pageToken)}` : ""}`,
    ),
  growthEvent: (orgId: string, event: GrowthClientEvent) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/growth/events`, { method: "POST", body: event }),

  // Growth Intelligence insights
  growthInsights: (orgId: string, filters: GrowthInsightFilters = {}) =>
    authed<{ items: GrowthInsightRow[]; total: number; next_page_token: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/insights${insightQuery(filters)}`,
    ),
  growthInsight: (orgId: string, insightId: string) =>
    authed<GrowthInsight>(P, `/v1/organizations/${enc(orgId)}/growth/insights/${enc(insightId)}`),
  growthSaved: (orgId: string, pageToken?: string, pageSize?: number) =>
    authed<{ items: GrowthInsightRow[]; next_page_token: string | null }>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/saved${insightQuery({ page_token: pageToken, page_size: pageSize })}`,
    ),
  growthSummary: (orgId: string) =>
    authed<GrowthSummary>(P, `/v1/organizations/${enc(orgId)}/growth/summary`),
  saveInsight: (orgId: string, insightId: string, saved: boolean) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/growth/insights/${enc(insightId)}/saved`, {
      method: saved ? "PUT" : "DELETE",
    }),
  dismissInsight: (orgId: string, insightId: string, reason: string) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/growth/insights/${enc(insightId)}/dismiss`, {
      method: "POST",
      body: { reason },
    }),
  undismissInsight: (orgId: string, insightId: string) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/growth/insights/${enc(insightId)}/dismiss`, {
      method: "DELETE",
    }),
  markInsightIrrelevant: (orgId: string, insightId: string, irrelevant: boolean) =>
    authed<void>(P, `/v1/organizations/${enc(orgId)}/growth/insights/${enc(insightId)}/irrelevant`, {
      method: irrelevant ? "POST" : "DELETE",
    }),
  reportInsight: (orgId: string, insightId: string, body: { reasons: string[]; details?: string }) =>
    authed<{ id: string; status: string; created_at: string }>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/insights/${enc(insightId)}/reports`,
      { method: "POST", body },
    ),
  // Growth Intelligence recommendations board
  growthBoard: (orgId: string, filters: GrowthBoardFilters = {}) =>
    authed<GrowthBoard>(P, `/v1/organizations/${enc(orgId)}/growth/recommendations${insightQuery(filters)}`),
  createTask: (
    orgId: string,
    body: {
      insight_action_id?: string;
      /** Links a from-scratch task to the insight it came from. */
      insight_id?: string;
      title?: string;
      description?: string;
      priority?: GrowthSeverity;
      assignee_user_ids?: string[];
      due_on?: string;
    },
  ) =>
    authed<GrowthTask>(P, `/v1/organizations/${enc(orgId)}/growth/recommendations`, { method: "POST", body }),
  updateTask: (
    orgId: string,
    taskId: string,
    body: Partial<Pick<GrowthTask, "title" | "description" | "priority" | "due_on" | "assignee_user_ids">> & {
      version?: number;
    },
  ) =>
    authed<GrowthTask>(P, `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}`, {
      method: "PATCH",
      body,
    }),
  growthTask: (orgId: string, taskId: string) =>
    authed<GrowthTaskDetail>(P, `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}`),
  /** Save the proposed change by hand (a preparer, an approver, or one of the card's assignees). */
  saveDraft: (
    orgId: string,
    taskId: string,
    body: {
      page_url: string;
      current_text: string;
      proposed_text: string;
      summary?: string;
      version?: number;
      builder_prompt?: string;
    },
  ) =>
    authed<GrowthDraft>(P, `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}/draft`, {
      method: "PUT",
      body,
    }),
  /** Ask the AI to draft the change; it comes back "drafting" and the worker fills it in. */
  requestAiDraft: (
    orgId: string,
    taskId: string,
    /** Without `current_text`, the change is found in the business's saved website (and gets a builder prompt). */
    body: { page_url?: string; current_text?: string; instructions?: string },
  ) =>
    authed<GrowthDraft>(P, `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}/draft/ai`, {
      method: "POST",
      body,
    }),
  growthWebsite: (orgId: string) =>
    authed<GrowthWebsite>(P, `/v1/organizations/${enc(orgId)}/growth/website`),
  taskAttachments: (orgId: string, taskId: string) =>
    authed<{ items: GrowthTaskAttachment[] }>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}/attachments`,
    ),
  uploadTaskAttachment: (
    orgId: string,
    taskId: string,
    file: File,
    options: { onProgress?: (sent: number, total: number) => void; signal?: AbortSignal } = {},
  ) => {
    const body = new FormData();
    body.append("file", file);
    return authed<GrowthTaskAttachment>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}/attachments`,
      { method: "POST", body, onUploadProgress: options.onProgress, signal: options.signal },
    );
  },
  /** The file's bytes, to open or preview (the API wants the access token, so a plain link won't do). */
  taskAttachmentFile: (orgId: string, taskId: string, attachmentId: string) =>
    authed<Blob>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}/attachments/${enc(attachmentId)}/content`,
      { responseType: "blob" },
    ),
  deleteTaskAttachment: (orgId: string, taskId: string, attachmentId: string) =>
    authed<void>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}/attachments/${enc(attachmentId)}`,
      { method: "DELETE" },
    ),
  /** Move a card through the pipeline; sending it back for revision needs a `message`. */
  /** Put the website back to the version live before this Live card's change was published (approvers). */
  undoTask: (orgId: string, taskId: string, version: number) =>
    authed<GrowthTask>(P, `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}/undo`, {
      method: "POST",
      body: { version },
    }),
  moveTask: (
    orgId: string,
    taskId: string,
    to: GrowthTaskStatus,
    options: { version?: number; message?: string; reason?: string; reasons?: string[] } = {},
  ) =>
    authed<GrowthTask>(
      P,
      `/v1/organizations/${enc(orgId)}/growth/recommendations/${enc(taskId)}/transitions`,
      {
        method: "POST",
        body: { to, ...options },
      },
    ),
  correctAssumption: (orgId: string, insightId: string, value: string, version: number) =>
    authed<GrowthInsight>(P, `/v1/organizations/${enc(orgId)}/growth/insights/${enc(insightId)}/assumption`, {
      method: "PUT",
      body: { value, version },
    }),
};

// ── Support (PHP-3773) ───────────────────────────────────────────────────────
export const support = {
  form: () => authed<SupportForm>(P, "/v1/support/form"),
  submissions: () => authed<SupportSubmissionList>(P, "/v1/support/submissions"),
  uploadFile: (fieldId: string, file: File, onProgress?: (sent: number, total: number) => void) => {
    const body = new FormData();
    body.append("fieldId", fieldId);
    body.append("file", file);
    return authed<SupportUpload>(P, "/v1/support/uploads", {
      method: "POST",
      body,
      onUploadProgress: onProgress,
    });
  },
  submit: (idempotencyKey: string, body: SupportSubmissionInput) =>
    authed<SupportSubmission>(P, "/v1/support/submissions", {
      method: "POST",
      body,
      headers: { "Idempotency-Key": idempotencyKey },
    }),
};

/** The insight filters as a query string; list filters repeat their key (`urgency=high&urgency=low`). */
export function insightQuery(filters: GrowthInsightFilters | GrowthBoardFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    for (const v of Array.isArray(value) ? value : [value]) {
      if (v !== undefined && v !== null && v !== "") params.append(key, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

// ── Website Runtime (direct) ────────────────────────────────────────────────
// External sites upload to the runtime itself, like the builder: the portal API takes small JSON
// bodies only. The runtime authorizes the same access token.
const R = config.runtimeUrl;

export const runtime = {
  /**
   * Uploads a static site's files as a new deployment (queued; poll the portal for progress).
   * `onProgress` gets the bytes sent so far and the total, so the page can show an upload bar.
   */
  deploy: (
    siteId: string,
    files: StaticFile[],
    onProgress?: (sent: number, total: number) => void,
    signal?: AbortSignal,
  ) =>
    authed<void>(R, `/v1/sites/${enc(siteId)}/deployments`, {
      method: "POST",
      body: { files },
      onUploadProgress: onProgress,
      signal,
    }),
  /** Public readiness probe: is hosting serving normally? */
  health: () => request<{ status: string }>(R, "/health/ready"),
};
